<template>
  <MarketingShell>
    <section class="tm-pricing-hero tm-container">
      <div class="tm-eyebrow">
        <span></span>SIMPLE PLANS. A CLEARER PERSPECTIVE.
      </div>
      <h1>Your workspace.<br /><span>Your pace.</span></h1>
      <p class="tm-hero-copy">
        Start with a free trial. Find your flow with Pro.<br />The tools stay
        together. You choose the time.
      </p>
      <div class="tm-billing-toggle" role="group" aria-label="Billing period">
        <button
          :class="{ active: billing === 'monthly' }"
          :aria-pressed="billing === 'monthly'"
          @click="billing = 'monthly'"
        >
          Monthly</button
        ><button
          :class="{ active: billing === 'yearly' }"
          :aria-pressed="billing === 'yearly'"
          @click="billing = 'yearly'"
        >
          Yearly <span>Save {{ savingPercent }}%</span>
        </button>
      </div>
    </section>
    <section class="tm-container tm-pricing-content">
      <div
        v-if="$clerk.isAuthenticated && $clerk.subscriptionValid"
        class="tm-active-notice"
        role="status"
      >
        <span class="tm-notice-check"><MarketingIcon name="check" /></span>
        <div>
          <strong>{{
            $clerk.subscriptionPlan === "trial"
              ? "Your trial is active"
              : "You’re already on Pro"
          }}</strong>
          <p v-if="$clerk.subscriptionExpiry">
            Your access runs through
            {{ formatDate($clerk.subscriptionExpiry) }}.
          </p>
        </div>
        <router-link to="/chart" class="tm-text-link"
          >Open charts <MarketingIcon name="arrow" :size="17"
        /></router-link>
      </div>
      <div class="tm-price-cards">
        <article class="tm-price-card tm-trial-card">
          <span class="tm-feature-icon"><MarketingIcon name="chart" /></span
          ><span class="tm-plan-label">A LITTLE ROOM TO EXPLORE</span>
          <h2>Find your flow</h2>
          <p>Get to know your next trading workspace.</p>
          <div class="tm-price-number">₹0<span>/ 7 days</span></div>
          <p class="tm-billing-note">
            No card required to activate your trial.
          </p>
          <button
            class="tm-button tm-button-secondary"
            :disabled="
              trialLoading ||
              (statusLoading && $clerk.isAuthenticated) ||
              trial.taken ||
              $clerk.subscriptionValid
            "
            @click="activateTrial"
          >
            {{
              trialLoading
                ? "Activating trial…"
                : statusLoading && $clerk.isAuthenticated
                  ? "Checking your account…"
                  : $clerk.subscriptionValid
                    ? "Access already active"
                    : trial.taken
                      ? "Trial already used"
                      : "Start your free trial"
            }}<MarketingIcon
              v-if="
                !trialLoading &&
                !statusLoading &&
                !trial.taken &&
                !$clerk.subscriptionValid
              "
              name="arrow"
              :size="16"
            />
          </button>
          <p v-if="trialError" class="tm-error" role="alert">
            {{ trialError }}
          </p>
          <ul class="tm-plan-checks">
            <li>
              <MarketingIcon name="check" :size="17" />Seven days of Pro
              charting tools
            </li>
            <li>
              <MarketingIcon name="check" :size="17" />50 AI credits for your
              trial
            </li>
            <li>
              <MarketingIcon name="check" :size="17" />Draw, replay and explore
              your ideas
            </li>
            <li>
              <MarketingIcon name="check" :size="17" />No automatic paid
              conversion
            </li>
          </ul>
          <p class="tm-card-footnote">Available once per eligible account.</p>
        </article>
        <article class="tm-price-card tm-pro-card">
          <div class="tm-pro-ribbon">
            <span>YOUR EVERYDAY WORKSPACE</span><span>TRADINGCANDLE PRO</span>
          </div>
          <span class="tm-feature-icon"><MarketingIcon name="layers" /></span
          ><span class="tm-plan-label">MORE TIME FOR YOUR PERSPECTIVE</span>
          <h2>Make it your own</h2>
          <p>All your Pro tools. One connected workspace.</p>
          <div class="tm-price-number">
            {{ formatPrice(plans[billing].amount)
            }}<span>/ {{ billing === "yearly" ? "year" : "30 days" }}</span>
          </div>
          <p class="tm-billing-note">
            {{
              billing === "yearly"
                ? "One payment for 365 days. Save " +
                  formatPrice(yearlySaving) +
                  " over twelve monthly purchases."
                : "One payment for 30 days of Pro access."
            }}
          </p>
          <button
            class="tm-button"
            :disabled="
              paymentLoading ||
              (statusLoading && $clerk.isAuthenticated)
            "
            @click="handlePayment(false)"
          >
            {{
              paymentLoading
                ? "Opening secure checkout…"
                : $clerk.subscriptionValid
                  ? "Renew TradingCandle Pro"
                  : "Get TradingCandle Pro"
            }}<MarketingIcon v-if="!paymentLoading" name="arrow" :size="16" />
          </button>
          <button v-if="$clerk.subscriptionValid && !trialStatus.active" class="tm-button" :disabled="paymentLoading" @click="handlePayment(true)">Add an AI subscription</button>
          <p class="tm-card-footnote">Includes 5 trial AI credits per subscribed user each month. More AI usage is an optional add-on.</p>
          <p v-if="paymentError" class="tm-error" role="alert">
            {{ paymentError }}
          </p>
          <ul class="tm-plan-checks">
            <li v-for="feature in pricingFeatures" :key="feature">
              <MarketingIcon name="check" :size="17" />{{ feature }}
            </li>
          </ul>
          <p class="tm-card-footnote">
            Secure checkout with PayU. Third-party market data is separate.
          </p>
        </article>
      </div>
      <p v-if="statusError" class="tm-status-error" role="alert">
        {{ statusError }}
        <button @click="checkStatus">Retry account check</button>
      </p>
      <div v-if="$clerk.isAuthenticated" class="tm-syfrus-offer">
        <SyfrusOffer @claimed="checkStatus" />
      </div>
      <div class="tm-pricing-reassurance">
        <span><MarketingIcon name="shield" :size="18" />Secure checkout</span
        ><span
          ><MarketingIcon name="wallet" :size="18" />Fixed access period</span
        ><span
          ><MarketingIcon name="cloud" :size="18" />Your workspace, saved</span
        >
      </div>
    </section>
    <section class="tm-container tm-section tm-included">
      <div class="tm-section-heading tm-heading-centered">
        <span class="tm-kicker">EVERY TOOL HAS ITS PLACE</span>
        <h2>Everything you need<br />to build your perspective.</h2>
        <p>
          Your Pro tools work together, from the first chart to your next
          review.
        </p>
      </div>
      <div class="tm-included-grid">
        <article v-for="feature in featureGroups" :key="feature.title">
          <MarketingIcon :name="feature.icon" />
          <h3>{{ feature.title }}</h3>
          <p>{{ feature.text }}</p>
        </article>
      </div>
    </section>
    <section class="tm-faq-section tm-container">
      <div>
        <span class="tm-kicker">A FEW THINGS TO KNOW</span>
        <h2>Clear answers.<br />No guesswork.</h2>
        <p>
          Need a hand?<br /><router-link to="/help" class="tm-text-link"
            >Visit our help center <MarketingIcon name="arrow" :size="16"
          /></router-link>
        </p>
      </div>
      <div class="tm-faq-list">
        <details v-for="faq in faqs" :key="faq.q">
          <summary>{{ faq.q }}<span>+</span></summary>
          <p>{{ faq.a }}</p>
        </details>
      </div>
    </section>
  </MarketingShell>
</template>
<script>
import MarketingShell from "./marketing/MarketingShell.vue";
import MarketingIcon from "./marketing/MarketingIcon.vue";
import SyfrusOffer from "./SyfrusOffer.vue";
import { initiatePayment, PLANS } from "../services/payu_service";
import {
  featureGroups,
  pricingFeatures,
  pricingFaqs,
} from "./marketing/content";
export default {
  name: "Pricing",
  components: { MarketingShell, MarketingIcon, SyfrusOffer },
  data: () => ({
    billing: "yearly",
    plans: PLANS,
    featureGroups,
    pricingFeatures,
    faqs: pricingFaqs,
    paymentLoading: false,
    paymentError: "",
    trial: { taken: false, active: false },
    trialLoading: false,
    trialError: "",
    statusLoading: false,
    statusError: "",
  }),
  computed: {
    yearlySaving() {
      return (
        Number(this.plans.monthly.amount) * 12 -
        Number(this.plans.yearly.amount)
      );
    },
    savingPercent() {
      return Math.round(
        (this.yearlySaving / (Number(this.plans.monthly.amount) * 12)) * 100,
      );
    },
  },
  mounted() {
    this.checkStatus();
  },
  methods: {
    formatPrice(amount) {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
      }).format(Number(amount));
    },
    formatDate(value) {
      return new Date(value).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    },
    async checkStatus() {
      this.statusLoading = true;
      this.statusError = "";
      try {
        await this.$clerk.init();
        if (!this.$clerk.isAuthenticated) return;
        const token = await this.$clerk.getToken();
        if (!token) return;
        const response = await fetch(
          `${process.env.VUE_APP_API_BASE || ""}/api/trial/status`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!response.ok)
          throw new Error("We couldn’t check trial eligibility. Please retry.");
        this.trial = await response.json();
      } catch (error) {
        this.statusError = error.message;
      } finally {
        this.statusLoading = false;
      }
    },
    async activateTrial() {
      if (!this.$clerk.isAuthenticated) {
        this.$clerk.openSignUp();
        return;
      }
      this.trialLoading = true;
      this.trialError = "";
      try {
        const token = await this.$clerk.getToken();
        if (!token) throw new Error("Please sign in again.");
        const response = await fetch(
          `${process.env.VUE_APP_API_BASE || ""}/api/trial/activate`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );
        const data = await response.json();
        if (!response.ok)
          throw new Error(data.error || "Unable to activate your trial.");
        this.trial = {
          taken: true,
          active: true,
          expiresAt: data.trial.expiresAt,
        };
        this.$clerk.setSubscription("trial", data.trial.expiresAt);
        this.$store.dispatch("activateSubscription", {
          plan: "trial",
          expiresAt: data.trial.expiresAt,
          txnId: "",
        });
        this.$router.push("/chart");
      } catch (error) {
        this.trialError = error.message;
      } finally {
        this.trialLoading = false;
      }
    },
    async handlePayment(aiOnly = false) {
      if (!this.$clerk.isAuthenticated) {
        this.$clerk.openSignUp();
        return;
      }
      this.paymentLoading = true;
      this.paymentError = "";
      try {
        const token = await this.$clerk.getToken();
        if (!token) throw new Error("Please sign in again.");
        await initiatePayment(this.billing, this.$clerk.user, token, aiOnly);
      } catch (error) {
        this.paymentError =
          error.response?.data?.error ||
          error.message ||
          "Unable to open checkout. Please try again.";
        this.paymentLoading = false;
      }
    },
  },
};
</script>
